
import { useEffect } from "react";
import { Message } from "@/services/messages";
import { useRealtimeMessages } from "./useRealtimeMessages";
import { usePaginatedMessages } from "./usePaginatedMessages";
import { useParticipantsData } from "./useParticipantsData";

interface UseConversationDataProps {
  userId: string;
  reagentId?: string;
}

interface UseConversationDataReturn {
  messages: Message[];
  username: string;
  reagentName?: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMessages: (pageNumber: number, append: boolean) => Promise<void>;
  loadMore: () => void;
  refreshConversation: () => void;
  isRealtimeConnected: boolean;
}

/**
 * Hook that combines data loading, pagination, and real-time functionality
 * for conversation data
 */
export function useConversationData({ 
  userId, 
  reagentId 
}: UseConversationDataProps): UseConversationDataReturn {
  // Get participant data (username and reagent information)
  const {
    username,
    reagentName
  } = useParticipantsData({ userId, reagentId });
  
  // Get paginated message loading functionality
  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMessages,
    loadMore,
    refreshConversation
  } = usePaginatedMessages({ userId, reagentId });
  
  // Initial load of conversation
  useEffect(() => {
    if (userId) {
      loadMessages(0, false);
    }
  }, [userId, loadMessages]);
  
  // Set up real-time message subscription
  const { isSubscribed } = useRealtimeMessages({
    otherUserId: userId,
    reagentId,
    refreshMessages: refreshConversation
  });

  return {
    messages,
    username,
    reagentName,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMessages,
    loadMore,
    refreshConversation,
    isRealtimeConnected: isSubscribed
  };
}
